import { useMemo, useState } from 'react';
import { Api } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const SUBJECT_KEYS = [
  'support.subject.other',
  'support.subject.order',
  'support.subject.payment',
  'support.subject.general',
];

const SupportPage = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    subject: SUBJECT_KEYS[0],
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [subjectPickerOpen, setSubjectPickerOpen] = useState(false);
  const subjectOptions = SUBJECT_KEYS.map((key) => ({
    key,
    label: t(key),
  }));

  const isFormValid = useMemo(() => {
    return form.name.trim() && form.phone.trim() && form.message.trim();
  }, [form]);

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid) {
      setStatus({
        type: 'error',
        text: t('support.form.validation_error'),
      });
      return;
    }

    try {
      setSubmitting(true);
      setStatus(null);
      await Api.sendContactMessage({
        name: form.name.trim(),
        phoneNumber: form.phone.trim(),
        email: form.email.trim() || null,
        subject: t(form.subject),
        message: form.message.trim(),
      });
      setForm({
        name: '',
        phone: '',
        email: '',
        subject: SUBJECT_KEYS[0],
        message: '',
      });
      setStatus({
        type: 'success',
        text: t('support.form.success'),
      });
    } catch (error) {
      setStatus({
        type: 'error',
        text: error.message || t('support.form.error_generic'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support">
      <section className="support__hero">
        
        <div className="support__hero-text">
          <h1>{t('support.title')}</h1>
          <p>{t('support.description')}</p>
        </div>
      </section>

      <section className="support__channels">
        <article>
          <span>{t('support.contact_us')}</span>
          <strong>055 283 0063</strong>
        </article>
        <article>
          <span>{t('support.email')}</span>
          <strong>info@wassiniLuxury.com</strong>
        </article>
      </section>

      <section className="support__form-wrapper">
        <form className="support-form" onSubmit={handleSubmit}>
          <label>
            <span>{t('support.form.name')}</span>
            <input
              type="text"
              placeholder={t('support.form.name_placeholder')}
              value={form.name}
              onChange={(event) => updateField('name', event.target.value)}
            />
          </label>

          <label>
            <span>{t('support.form.phone')}</span>
            <input
              type="tel"
              placeholder={t('support.form.phone_placeholder')}
              value={form.phone}
              onChange={(event) => updateField('phone', event.target.value)}
            />
          </label>

          <label>
            <span>{t('support.form.email')}</span>
            <input
              type="email"
              placeholder={t('support.form.email_placeholder')}
              value={form.email}
              onChange={(event) => updateField('email', event.target.value)}
            />
          </label>

          <label className="support-form__select">
            <span>{t('support.form.subject')}</span>
            <button
              type="button"
              onClick={() => setSubjectPickerOpen(true)}
            >
              {t(form.subject)}
            </button>
          </label>

          <label>
            <span>{t('support.form.message')}</span>
            <textarea
              rows={5}
              placeholder={t('support.form.message_placeholder')}
              value={form.message}
              onChange={(event) => updateField('message', event.target.value)}
            />
          </label>

          {status && (
            <p className={`support-form__status support-form__status--${status.type}`}>
              {status.text}
            </p>
          )}

          <button type="submit" className="button" disabled={submitting}>
            {submitting ? t('support.form.submitting') : t('support.form.submit')}
          </button>
        </form>
      </section>

      {subjectPickerOpen && (
        <div className="support-picker">
          <div
            className="support-picker__backdrop"
            onClick={() => setSubjectPickerOpen(false)}
          />
          <div className="support-picker__sheet">
            <header>
              <h3>{t('support.subject_picker.title')}</h3>
              <button
                type="button"
                className="button button--ghost"
                onClick={() => setSubjectPickerOpen(false)}
              >
                {t('support.subject_picker.close')}
              </button>
            </header>
            {subjectOptions.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  updateField('subject', key);
                  setSubjectPickerOpen(false);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPage;
